import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import SchemaOrg from "@/components/base/SchemaOrg";
import { categories, getCategoryBySlug } from "@/mocks/categories";
import { getArticlesByCategory } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = getCategoryBySlug(slug || "");
  const articles = getArticlesByCategory(slug || "");

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
              <i className="ri-folder-open-line text-3xl text-secondary-500"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">分类未找到</h1>
            <p className="text-foreground-600 mb-6">您访问的资源分类不存在</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: currentCategory.name },
  ];

  const breadcrumbSchema = {
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://www.haijiao-resource.com/" },
      { "@type": "ListItem", position: 2, name: currentCategory.name, item: `https://www.haijiao-resource.com/category/${slug}` },
    ],
  };

  // Get other categories for sidebar
  const otherCategories = categories.filter((cat) => cat.slug !== slug);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema as Record<string, unknown>} />
      <SchemaOrg type="WebPage" data={{ name: `${currentCategory.name} - 海角千万资源原创`, url: `https://www.haijiao-resource.com/category/${slug}` }} />

      <Navbar />

      <main className="pt-16">
        {/* Category Header */}
        <section className="relative py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-100">
                <i className={`${currentCategory.icon} text-2xl md:text-3xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {currentCategory.name}
                </h1>
                <p className="text-foreground-600 text-sm mt-1">
                  {currentCategory.count.toLocaleString()} 件原创资源
                </p>
              </div>
            </div>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl">
              {currentCategory.description}
            </p>
          </div>
        </section>

        {/* Content Area */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10">
              {/* Articles */}
              <div className="flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {articles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group rounded-2xl bg-background-100 overflow-hidden hover:bg-background-200 transition-colors duration-200 cursor-pointer"
                      onClick={() => window.scrollTo({ top: 0, behavior: "instant" })}
                    >
                      <div className="w-full h-48">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <h2 className="font-heading text-base font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors duration-200">
                          {article.title}
                        </h2>
                        <p className="text-foreground-600 text-sm line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-4 text-foreground-500 text-xs">
                          <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                          <span>{article.date}</span>
                          <span><i className="ri-eye-line mr-1"></i>{article.views.toLocaleString()}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {articles.length === 0 && (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                      <i className="ri-inbox-line text-2xl text-secondary-500"></i>
                    </div>
                    <p className="text-foreground-600">该分类暂无内容，敬请期待</p>
                  </div>
                )}
              </div>

              {/* Sidebar - Category Navigation */}
              <aside className="w-full lg:w-64 flex-shrink-0">
                <div className="sticky top-24">
                  <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-4">
                    浏览其他分类
                  </h4>
                  <div className="flex flex-col gap-2">
                    {otherCategories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer"
                        onClick={() => window.scrollTo({ top: 0, behavior: "instant" })}
                      >
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100/80">
                          <i className={`${cat.icon} text-primary-500 text-sm`}></i>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-foreground-800">{cat.name}</div>
                          <div className="text-foreground-500 text-xs">{cat.count.toLocaleString()} 件</div>
                        </div>
                      </Link>
                    ))}
                  </div>

                  <div className="mt-8 p-4 rounded-xl bg-secondary-100/70 border border-secondary-200/50">
                    <p className="text-sm text-foreground-700 font-medium mb-2">需要帮助？</p>
                    <p className="text-xs text-foreground-500 mb-3">
                      如果您需要特定类型的内容资源，欢迎联系我们定制。
                    </p>
                    <Link
                      to="/about"
                      className="inline-block px-4 py-1.5 bg-secondary-500 text-background-50 rounded-full text-xs font-medium hover:bg-secondary-600 transition-colors duration-200 whitespace-nowrap"
                    >
                      联系我们
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}